import type {
  Action,
  ValidationError,
  ValidationResult,
} from "../../types/actions";
import type { Project } from "../../types/project";
import type { Clip, Track } from "../../types/timeline";
import type {
  MutableClip,
  MutableTimeline,
  MutableTrack,
} from "../../utils/immutable-updates";
import { createInverseAction, registerActionHandler } from "../registry";
import type { ActionHandler, ActionHandlerContext } from "../registry";

/**
 * The track domain, moved off the three hand-written switches.
 *
 * Three types were asymmetric across those switches, and each asymmetry is a
 * spent undo press rather than a cosmetic gap:
 *
 * track/restore and track/restorePositions were applied by the executor and
 * appeared in neither of the other two. They were applied unvalidated, and the
 * executor pushed a history entry for them with a null inverse, so undo popped
 * the entry onto the redo stack, restored nothing, and swallowed the press.
 * Both have a validate and an invert below.
 *
 * track/consolidate had an executor case and an inverse case but no validator
 * case, so a consolidate naming a track that is not there reported success,
 * changed nothing, and pushed an entry whose inverse the generator returns null
 * for. It is rejected now, which is the one accept/reject change in this move.
 *
 * Ids here already came from crypto.randomUUID, so nothing in this domain
 * needed the Date.now collision fix the marker and effect ids got.
 *
 * track/volume is deliberately absent: action-executor.ts registers it, and a
 * second registration for the same type would leave the winner to import order.
 */

const TRACK_TYPES: readonly string[] = [
  "video",
  "audio",
  "image",
  "text",
  "graphics",
];

const TRACK_NAMES: Record<string, string> = {
  video: "Video",
  audio: "Audio",
  image: "Image",
  text: "Text",
  graphics: "Graphics",
};

function toResult(errors: ValidationError[]): ValidationResult {
  return { valid: errors.length === 0, errors };
}

function findTrack(project: Project, trackId: string): Track | null {
  return project.timeline.tracks.find((t) => t.id === trackId) ?? null;
}

function mapTracks(
  project: Project,
  fn: (track: MutableTrack) => MutableTrack,
): void {
  const timeline = project.timeline as MutableTimeline;
  timeline.tracks = timeline.tracks.map(fn);
}

function trackIdErrors(trackId: unknown, project: Project): ValidationError[] {
  if (!trackId || typeof trackId !== "string") {
    return [
      {
        code: "INVALID_PARAMS",
        message: "Track ID is required and must be a string",
        path: "params.trackId",
      },
    ];
  }
  if (!findTrack(project, trackId)) {
    return [
      {
        code: "TRACK_NOT_FOUND",
        message: `Track with ID ${trackId} not found`,
        path: "params.trackId",
      },
    ];
  }
  return [];
}

/**
 * Every field is spread first so nothing is silently dropped, then the mutable
 * nested structures are copied: the clip goes into a history entry that has to
 * outlive every later edit to the live project.
 */
function cloneClip(clip: Clip): Record<string, unknown> {
  return {
    ...(clip as unknown as Record<string, unknown>),
    effects: clip.effects.map((e) => ({ ...e, params: { ...e.params } })),
    audioEffects: clip.audioEffects.map((e) => ({
      ...e,
      params: { ...e.params },
    })),
    transform: { ...clip.transform },
    fade: clip.fade ? { ...clip.fade } : undefined,
    automation: clip.automation
      ? {
          volume: clip.automation.volume?.map((p) => ({ ...p })),
          pan: clip.automation.pan?.map((p) => ({ ...p })),
        }
      : undefined,
    keyframes: clip.keyframes.map((kf) => ({ ...kf })),
    emphasisAnimation: clip.emphasisAnimation
      ? { ...clip.emphasisAnimation }
      : undefined,
    metadata: clip.metadata ? { ...clip.metadata } : undefined,
  };
}

/**
 * Ids are kept because undo puts the same track back, not a new one. The field
 * list is the one the inverse generator has always used and is narrower than
 * Track, so a restored track comes back without its `volume` or its `groupId`.
 * Left as it was: changing what an undo restores is not part of this move.
 */
function cloneTrack(track: Track): Record<string, unknown> {
  return {
    id: track.id,
    type: track.type,
    name: track.name,
    clips: track.clips.map((c) => cloneClip(c)),
    transitions: track.transitions?.map((t) => ({ ...t })) ?? [],
    locked: track.locked,
    hidden: track.hidden,
    muted: track.muted,
    solo: track.solo,
  };
}

const trackAdd: ActionHandler = {
  type: "track/add",

  validate(action: Action): ValidationResult {
    const params = action.params as { trackType?: unknown; position?: unknown };
    const errors: ValidationError[] = [];

    if (
      typeof params.trackType !== "string" ||
      !TRACK_TYPES.includes(params.trackType)
    ) {
      errors.push({
        code: "INVALID_PARAMS",
        message:
          "Track type must be 'video', 'audio', 'image', 'text', or 'graphics'",
        path: "params.trackType",
      });
    }

    if (
      params.position !== undefined &&
      (typeof params.position !== "number" || params.position < 0)
    ) {
      errors.push({
        code: "INVALID_PARAMS",
        message: "Track position must be a non-negative number",
        path: "params.position",
      });
    }

    return toResult(errors);
  },

  apply(action: Action, project: Project, ctx: ActionHandlerContext): void {
    const params = action.params as {
      trackType: string;
      position?: number;
      trackId?: string;
    };
    const timeline = project.timeline as MutableTimeline;

    const trackCount =
      timeline.tracks.filter((t: MutableTrack) => t.type === params.trackType)
        .length + 1;
    const newTrack: MutableTrack = {
      id: params.trackId ?? `track-${crypto.randomUUID()}`,
      type: params.trackType as Track["type"],
      name: `${TRACK_NAMES[params.trackType] || params.trackType} ${trackCount}`,
      clips: [],
      transitions: [],
      locked: false,
      hidden: false,
      muted: false,
      solo: false,
    };

    const position =
      params.position !== undefined ? params.position : timeline.tracks.length;

    timeline.tracks = [
      ...timeline.tracks.slice(0, position),
      newTrack,
      ...timeline.tracks.slice(position),
    ];
    ctx.lastAddedIds.set("track", newTrack.id);
  },

  invert(action: Action): Action | null {
    // The id is not known until apply has run, so the executor resolves this
    // marker against lastAddedIds before the inverse is stored.
    return createInverseAction(action, "track/remove", {
      trackId: "__LAST_ADDED__",
    });
  },
};

const trackDuplicate: ActionHandler = {
  type: "track/duplicate",

  validate(action: Action, project: Project): ValidationResult {
    const params = action.params as {
      sourceTrackId?: unknown;
      position?: unknown;
    };
    const errors: ValidationError[] = [];

    if (!params.sourceTrackId || typeof params.sourceTrackId !== "string") {
      errors.push({
        code: "INVALID_PARAMS",
        message: "Source track ID is required and must be a string",
        path: "params.sourceTrackId",
      });
    } else if (!findTrack(project, params.sourceTrackId)) {
      errors.push({
        code: "TRACK_NOT_FOUND",
        message: `Track with ID ${params.sourceTrackId} not found`,
        path: "params.sourceTrackId",
      });
    }

    if (
      params.position !== undefined &&
      (typeof params.position !== "number" ||
        params.position < 0 ||
        params.position > project.timeline.tracks.length)
    ) {
      errors.push({
        code: "INVALID_PARAMS",
        message: "Track position must be within the timeline",
        path: "params.position",
      });
    }

    return toResult(errors);
  },

  apply(action: Action, project: Project, ctx: ActionHandlerContext): void {
    const params = action.params as {
      sourceTrackId: string;
      position?: number;
      trackId?: string;
    };
    const timeline = project.timeline as MutableTimeline;

    const sourceIndex = timeline.tracks.findIndex(
      (track: MutableTrack) => track.id === params.sourceTrackId,
    );
    if (sourceIndex < 0) return;
    const source = timeline.tracks[sourceIndex];
    const trackId = params.trackId ?? `track-${crypto.randomUUID()}`;
    const clipIdMap = new Map(
      source.clips.map((clip) => [clip.id, `clip-${crypto.randomUUID()}`]),
    );
    const names = new Set(timeline.tracks.map((track) => track.name));
    const baseName = `${source.name} Copy`;
    let name = baseName;
    let suffix = 2;
    while (names.has(name)) {
      name = `${baseName} ${suffix}`;
      suffix += 1;
    }
    const duplicate: MutableTrack = {
      ...structuredClone(source),
      id: trackId,
      name,
      clips: source.clips.map((clip) => ({
        ...structuredClone(clip),
        id: clipIdMap.get(clip.id)!,
        trackId,
      })),
      transitions: source.transitions.map((transition) => ({
        ...structuredClone(transition),
        id: `transition-${crypto.randomUUID()}`,
        clipAId: clipIdMap.get(transition.clipAId) ?? transition.clipAId,
        clipBId: transition.clipBId
          ? (clipIdMap.get(transition.clipBId) ?? transition.clipBId)
          : undefined,
      })),
    };
    const position = params.position ?? sourceIndex + 1;
    timeline.tracks = [
      ...timeline.tracks.slice(0, position),
      duplicate,
      ...timeline.tracks.slice(position),
    ];
    ctx.lastAddedIds.set("track", trackId);
  },

  invert(action: Action): Action | null {
    return createInverseAction(action, "track/remove", {
      trackId: "__LAST_ADDED__",
    });
  },
};

const trackRemove: ActionHandler = {
  type: "track/remove",

  validate(action: Action, project: Project): ValidationResult {
    const params = action.params as { trackId?: unknown };
    return toResult(trackIdErrors(params.trackId, project));
  },

  apply(action: Action, project: Project): void {
    const params = action.params as { trackId: string };
    const timeline = project.timeline as MutableTimeline;
    timeline.tracks = timeline.tracks.filter(
      (t: MutableTrack) => t.id !== params.trackId,
    );
  },

  invert(action: Action, projectBefore: Project): Action | null {
    const params = action.params as { trackId: string };
    const removedTrack = findTrack(projectBefore, params.trackId);
    if (!removedTrack) return null;

    const position = projectBefore.timeline.tracks.findIndex(
      (t) => t.id === params.trackId,
    );

    return createInverseAction(action, "track/restore", {
      track: cloneTrack(removedTrack),
      position,
    });
  },
};

/**
 * Only ever minted by the inverse of a removal, which is why it had no case in
 * either the validator or the inverse generator. It still reaches validate when
 * something replays a history entry through execute, so the checks here are the
 * ones apply silently depends on: an undefined position makes both slice calls
 * return the whole array and the track lands twice.
 */
const trackRestore: ActionHandler = {
  type: "track/restore",

  validate(action: Action): ValidationResult {
    const params = action.params as { track?: unknown; position?: unknown };
    const errors: ValidationError[] = [];

    const track = params.track as Track | undefined;
    if (!track || typeof track.id !== "string") {
      errors.push({
        code: "INVALID_PARAMS",
        message: "track/restore requires a track with an id",
        path: "params.track",
      });
    }

    if (typeof params.position !== "number" || params.position < 0) {
      errors.push({
        code: "INVALID_PARAMS",
        message: "Track position must be a non-negative number",
        path: "params.position",
      });
    }

    return toResult(errors);
  },

  apply(action: Action, project: Project): void {
    const params = action.params as { track: Track; position: number };
    const timeline = project.timeline as MutableTimeline;
    timeline.tracks = [
      ...timeline.tracks.slice(0, params.position),
      params.track,
      ...timeline.tracks.slice(params.position),
    ];
  },

  invert(action: Action): Action | null {
    const track = (action.params as { track: Track }).track;
    return createInverseAction(action, "track/remove", { trackId: track.id });
  },
};

const trackReorder: ActionHandler = {
  type: "track/reorder",

  validate(action: Action, project: Project): ValidationResult {
    const params = action.params as {
      trackId?: unknown;
      newPosition?: unknown;
    };
    const errors: ValidationError[] = trackIdErrors(params.trackId, project);

    if (
      typeof params.newPosition !== "number" ||
      params.newPosition < 0 ||
      params.newPosition >= project.timeline.tracks.length
    ) {
      errors.push({
        code: "INVALID_PARAMS",
        message: `New position must be between 0 and ${
          project.timeline.tracks.length - 1
        }`,
        path: "params.newPosition",
      });
    }

    return toResult(errors);
  },

  apply(action: Action, project: Project): void {
    const params = action.params as { trackId: string; newPosition: number };
    const timeline = project.timeline as MutableTimeline;
    const trackIndex = timeline.tracks.findIndex(
      (t: MutableTrack) => t.id === params.trackId,
    );
    if (trackIndex !== -1) {
      const [track] = timeline.tracks.splice(trackIndex, 1);
      timeline.tracks.splice(params.newPosition, 0, track);
    }
  },

  invert(action: Action, projectBefore: Project): Action | null {
    const params = action.params as { trackId: string };
    const currentPosition = projectBefore.timeline.tracks.findIndex(
      (t) => t.id === params.trackId,
    );
    if (currentPosition === -1) return null;

    return createInverseAction(action, "track/reorder", {
      trackId: params.trackId,
      newPosition: currentPosition,
    });
  },
};

interface TrackFlagSpec {
  readonly type: string;
  readonly paramKey: "locked" | "hidden" | "muted" | "solo";
  readonly label: string;
}

function makeTrackFlagHandler(spec: TrackFlagSpec): ActionHandler {
  return {
    type: spec.type,

    validate(action: Action, project: Project): ValidationResult {
      const params = action.params as Record<string, unknown>;
      // The id complaint comes first because the executor joins these messages
      // in order into the single string it reports back.
      const errors = trackIdErrors(params.trackId, project);

      if (typeof params[spec.paramKey] !== "boolean") {
        errors.push({
          code: "INVALID_PARAMS",
          message: `${spec.label} parameter must be a boolean`,
          path: `params.${spec.paramKey}`,
        });
      }

      return toResult(errors);
    },

    apply(action: Action, project: Project): void {
      const params = action.params as Record<string, unknown>;
      const trackId = params.trackId as string;
      const patch = {
        [spec.paramKey]: params[spec.paramKey],
      } as Partial<MutableTrack>;
      mapTracks(project, (t: MutableTrack) =>
        t.id === trackId ? { ...t, ...patch } : t,
      );
    },

    invert(action: Action, projectBefore: Project): Action | null {
      const trackId = (action.params as { trackId: string }).trackId;
      const track = findTrack(projectBefore, trackId);
      if (!track) return null;

      return createInverseAction(action, spec.type, {
        trackId,
        [spec.paramKey]: track[spec.paramKey],
      });
    },
  };
}

const trackRename: ActionHandler = {
  type: "track/rename",

  validate(action: Action, project: Project): ValidationResult {
    const params = action.params as { trackId?: unknown; name?: unknown };
    const errors = trackIdErrors(params.trackId, project);

    if (typeof params.name !== "string" || params.name.trim().length === 0) {
      errors.push({
        code: "INVALID_PARAMS",
        message: "Track name is required and must be a non-empty string",
        path: "params.name",
      });
    }

    return toResult(errors);
  },

  apply(action: Action, project: Project): void {
    const params = action.params as { trackId: string; name: string };
    mapTracks(project, (t: MutableTrack) =>
      t.id === params.trackId ? { ...t, name: params.name } : t,
    );
  },

  invert(action: Action, projectBefore: Project): Action | null {
    const params = action.params as { trackId: string };
    const track = findTrack(projectBefore, params.trackId);
    if (!track) return null;

    return createInverseAction(action, "track/rename", {
      trackId: params.trackId,
      name: track.name,
    });
  },
};

/**
 * The validator had no case for this one, so a consolidate naming a track that
 * is not there passed, applied nothing, and still took a history entry whose
 * inverse is null. Rejecting it is the one accept/reject change in this move.
 */
const trackConsolidate: ActionHandler = {
  type: "track/consolidate",

  validate(action: Action, project: Project): ValidationResult {
    const params = action.params as { trackId?: unknown };
    return toResult(trackIdErrors(params.trackId, project));
  },

  apply(action: Action, project: Project): void {
    const params = action.params as { trackId: string };
    mapTracks(project, (t: MutableTrack) => {
      if (t.id !== params.trackId) return t;
      // Timeline order, not array order: the clips on a track are not held
      // sorted, so packing them as stored would reshuffle the cut.
      const sorted = [...t.clips].sort((a, b) => a.startTime - b.startTime);
      let cursor = 0;
      const newPositions = new Map<string, number>();
      for (const c of sorted) {
        const newStart = Math.max(0, cursor);
        newPositions.set(c.id, newStart);
        cursor = newStart + c.duration;
      }
      return {
        ...t,
        clips: t.clips.map((c: MutableClip) => {
          const ns = newPositions.get(c.id);
          return ns !== undefined && ns !== c.startTime
            ? { ...c, startTime: ns }
            : c;
        }),
      };
    });
  },

  invert(action: Action, projectBefore: Project): Action | null {
    const trackId = (action.params as { trackId: string }).trackId;
    const track = findTrack(projectBefore, trackId);
    if (!track) return null;
    const positions = track.clips.map((c) => ({
      clipId: c.id,
      startTime: c.startTime,
    }));
    return createInverseAction(action, "track/restorePositions", {
      trackId,
      positions,
    });
  },
};

/**
 * The inverse of track/consolidate, and also of clip/closeGapBefore, which
 * still mints it from the legacy generator.
 */
const trackRestorePositions: ActionHandler = {
  type: "track/restorePositions",

  validate(action: Action, project: Project): ValidationResult {
    const params = action.params as { trackId?: unknown; positions?: unknown };
    const errors = trackIdErrors(params.trackId, project);

    if (!Array.isArray(params.positions)) {
      errors.push({
        code: "INVALID_PARAMS",
        message: "Positions is required and must be an array",
        path: "params.positions",
      });
    }

    return toResult(errors);
  },

  apply(action: Action, project: Project): void {
    const params = action.params as {
      trackId: string;
      positions: Array<{ clipId: string; startTime: number }>;
    };
    const lookup = new Map(
      params.positions.map((p) => [p.clipId, p.startTime] as const),
    );
    mapTracks(project, (t: MutableTrack) => {
      if (t.id !== params.trackId) return t;
      return {
        ...t,
        clips: t.clips.map((c: MutableClip) => {
          const ns = lookup.get(c.id);
          return ns !== undefined && ns !== c.startTime
            ? { ...c, startTime: ns }
            : c;
        }),
      };
    });
  },

  invert(action: Action, projectBefore: Project): Action | null {
    const trackId = (action.params as { trackId: string }).trackId;
    const track = findTrack(projectBefore, trackId);
    if (!track) return null;
    const positions = track.clips.map((c) => ({
      clipId: c.id,
      startTime: c.startTime,
    }));
    return createInverseAction(action, "track/restorePositions", {
      trackId,
      positions,
    });
  },
};

for (const handler of [
  trackAdd,
  trackDuplicate,
  trackRemove,
  trackRestore,
  trackReorder,
  trackRename,
  trackConsolidate,
  trackRestorePositions,
  makeTrackFlagHandler({
    type: "track/lock",
    paramKey: "locked",
    label: "Locked",
  }),
  makeTrackFlagHandler({
    type: "track/hide",
    paramKey: "hidden",
    label: "Hidden",
  }),
  makeTrackFlagHandler({
    type: "track/mute",
    paramKey: "muted",
    label: "Muted",
  }),
  makeTrackFlagHandler({ type: "track/solo", paramKey: "solo", label: "Solo" }),
]) {
  registerActionHandler(handler);
}
